import { ContentClientError } from './content-client-error';

/**
 * Options for API client requests
 */
export interface ScormClientOptions {
  /** ID of the enrollment */
  enrollment_id: number;
  /** ID of the course */
  course_id: number;
  /** ID of the component */
  component_id: number;
}

/**
 * Request parameters for content API requests
 */
export interface ContentRequestParams {
  /** Endpoint path for token retrieval */
  tokenEndpoint: string;
  /** Endpoint path for content data retrieval */
  contentEndpoint: string;
  /** API options containing IDs */
  options: ScormClientOptions;
  /** Validation function for the response data (optional) */
  validateResponse?: (data: unknown) => unknown;
}

// Type aliases for specific client implementations
export type ScormContentData = Record<string, unknown>;

/**
 * Abstract base class for SCORM/TinCan content API clients
 * This class serves as both the abstract base class and interface definition
 * for SCORM and TinCan content API clients, simplifying the architecture.
 */
export abstract class ScormContentClientBase<T> {
  /**
   * Get content data for launching
   * @param opts - Options containing enrollment_id, course_id and component_id
   * @returns Content data response from the API
   */
  abstract getContentData(opts: ScormClientOptions): Promise<T>;

  /**
   * Handles a content API request, including token retrieval, content data fetching,
   * and response validation and processing
   *
   * @param params - Parameters for the content request
   * @returns Processed content data
   * @throws Error if any step in the request process fails
   */
  protected async handleRequest<R>(params: ContentRequestParams): Promise<R> {
    try {
      // Get the security token
      const scorm_token = await this.getToken(params.tokenEndpoint, params.options);

      // Build the launch URL and fetch content data
      const launchUrl = this.buildLaunchUrl(params.contentEndpoint, scorm_token, params.options);
      const response = await fetch(launchUrl);

      if (!response.ok) {
        throw new ContentClientError(`Failed to fetch content data: ${response.status} ${response.statusText}`);
      }

      // Parse the response
      const data = await response.json();

      // Apply validation if provided
      let validatedData: unknown;
      if (params.validateResponse) {
        try {
          validatedData = params.validateResponse(data);
        } catch (error) {
          throw new ContentClientError('Invalid content data format', error);
        }
      } else {
        validatedData = data;
      }

      // Add the token to the returned data (cast to any to allow for generic return type)
      return {
        ...(validatedData as Record<string, unknown>),
        scorm_token,
      } as R;
    } catch (error) {
      // Re-throw ContentClientErrors, wrap other errors
      if (error instanceof ContentClientError) {
        throw error;
      }
      throw new ContentClientError('Unexpected error during content request', error);
    }
  }

  /**
   * Fetches a security token required for content launch
   *
   * @param endpointPath - The API endpoint path for token retrieval
   * @param opts - Options containing enrollment_id, course_id and component_id
   * @returns Security token string
   */
  protected async getToken(endpointPath: string, opts: ScormClientOptions): Promise<string> {
    const launch_url = `${endpointPath}?enroll_id=${opts.enrollment_id}&course_id=${opts.course_id}&component_id=${opts.component_id}`;

    try {
      const response = await fetch(launch_url);

      if (!response.ok) {
        throw new ContentClientError(`Failed to fetch token: ${response.status} ${response.statusText}`);
      }

      const data = await response.json();

      if (!data || !data.scorm_token) {
        throw new ContentClientError('Failed to fetch token. Please check the enrollment and component IDs.');
      }

      return data.scorm_token;
    } catch (error) {
      if (error instanceof ContentClientError) {
        throw error;
      }
      throw new ContentClientError('Failed to fetch token', error);
    }
  }

  /**
   * Builds a launch URL for content
   *
   * @param apiEndpoint - The API endpoint for content launch
   * @param token - The security token for the content launch
   * @param opts - Options containing IDs
   * @returns Formatted launch URL
   */
  protected buildLaunchUrl(apiEndpoint: string, token: string, opts: ScormClientOptions): string {
    return `${apiEndpoint}?slt=${token}&comp_id=${opts.component_id}&enroll_id=${opts.enrollment_id}&course_id=${opts.course_id}`;
  }
}
